import { useMemo } from 'react'
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Badge,
    Box,
    Flex,
    Heading,
    Spinner,
    Stack,
    Text
} from '@chakra-ui/react'
import { FiCheckCircle } from 'react-icons/fi'
import { PROTECTED_ROUTES } from '@/shared/config/routes'
import { ProtectedPage } from '@/shared/components/ProtectedPage'
import { useDuplicateParties } from '@/shared/api/hooks/useDuplicateParties'
import { useMyDuplicates } from '@/shared/api/hooks/useParentDuplicates'
import DuplicateCompare from '@/pages/user/teacher/duplicates/components/DuplicateCompare'
import {
    STATUS_COLOR,
    STATUS_LABEL,
    duplicateStatus,
    formatDateTime,
    partyIds
} from '@/pages/user/teacher/duplicates/types'

/**
 * The parent-facing side of duplicate handling, reached from the "suspected
 * duplicate account" notification.
 *
 * A parent normally has one pair at most, so there is no list grid here: every
 * pair awaiting review is laid out as its own side-by-side comparison, and the
 * parent picks which record to keep. Anything already resolved is summarised
 * underneath so the page still explains itself on a second visit.
 */
const MyDuplicates = () => {
    const { duplicates, parentId, loading, error } = useMyDuplicates()
    // A parent has one pair at most, and every side resolves from a single
    // `GET /parents/by-name` call, so hydrating all of them at once is cheap.
    const {
        parties,
        loading: hydrating,
        error: pairError
    } = useDuplicateParties(partyIds(duplicates))

    const { pending, resolved } = useMemo(
        () => ({
            pending: duplicates.filter((record) => !record.processed),
            resolved: duplicates.filter((record) => record.processed)
        }),
        [duplicates]
    )

    return (
        <ProtectedPage routeConfig={PROTECTED_ROUTES['/user/parent/duplicates']}>
            <Box
                borderRadius={4}
                bg={'white'}
                mx={'auto'}
                py={5}
                px={{ base: 2, md: 8 }}
                minH={{ base: 'auto', md: '520px' }}
            >
                <Box p="2" mb={2}>
                    <Heading size="md" my={2}>
                        Duplicate account review
                    </Heading>
                    <Text color={'gray.600'}>
                        We found more than one account with your name and phone number.
                        Choose the record to keep — the other one is merged into it, so
                        your children and their check-in history all end up in one place.
                    </Text>
                </Box>

                {error ? (
                    <Alert status="error" borderRadius="md" fontSize="sm">
                        <AlertIcon />
                        <AlertDescription>{error.message}</AlertDescription>
                    </Alert>
                ) : loading ? (
                    <Flex justify="center" align="center" height="300px">
                        <Spinner size="xl" />
                    </Flex>
                ) : pending.length === 0 ? (
                    <Flex
                        direction="column"
                        align="center"
                        justify="center"
                        gap={2}
                        py={12}
                        borderWidth="1px"
                        borderStyle="dashed"
                        borderColor="gray.200"
                        borderRadius="md"
                        textAlign="center"
                    >
                        <Box as={FiCheckCircle} fontSize="2xl" color="green.400" />
                        <Text fontWeight="medium">Nothing to review</Text>
                        <Text color="gray.500" fontSize="sm" maxW="md">
                            {/* A user with no Parent row can't have duplicates —
                                say so plainly rather than implying a check ran. */}
                            {parentId
                                ? 'None of your accounts are flagged as duplicates right now.'
                                : 'You have no parent record yet, so there is nothing to compare.'}
                        </Text>
                    </Flex>
                ) : (
                    <Stack spacing={6}>
                        {pending.map((record) => (
                            <Box
                                key={record._id}
                                borderWidth="1px"
                                borderColor="gray.100"
                                borderRadius="md"
                            >
                                <DuplicateCompare
                                    record={record}
                                    parties={parties}
                                    loading={hydrating}
                                    loadError={pairError}
                                    ownParentId={parentId}
                                />
                            </Box>
                        ))}
                    </Stack>
                )}

                {resolved.length > 0 ? (
                    <Box mt={8} pt={4} borderTopWidth="1px" borderColor="gray.100">
                        <Heading size="sm" mb={3}>
                            Already reviewed
                        </Heading>
                        <Stack spacing={2}>
                            {resolved.map((record) => {
                                const status = duplicateStatus(record)
                                return (
                                    <Flex
                                        key={record._id}
                                        align="center"
                                        gap={3}
                                        borderWidth="1px"
                                        borderColor="gray.100"
                                        borderRadius="md"
                                        px={4}
                                        py={3}
                                        flexWrap="wrap"
                                    >
                                        <Badge
                                            colorScheme={STATUS_COLOR[status]}
                                            borderRadius="full"
                                            px={2}
                                        >
                                            {STATUS_LABEL[status]}
                                        </Badge>
                                        <Text fontSize="sm" color="gray.600">
                                            {status === 'merged'
                                                ? 'Your accounts were merged into one.'
                                                : 'Marked as two separate people.'}
                                        </Text>
                                        <Box flex="1" />
                                        <Text fontSize="xs" color="gray.500">
                                            {formatDateTime(record.processedAt)}
                                        </Text>
                                    </Flex>
                                )
                            })}
                        </Stack>
                    </Box>
                ) : null}
            </Box>
        </ProtectedPage>
    )
}

export default MyDuplicates
